import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import qs from 'qs';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { getEmailStatus } from '../../../helpers/email.helpers';
import { GLOBAL_ERROR_CODES } from '../../../helpers/global.constants';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { IStorageService } from '../../../services/storage.service';
import { IUserService } from '../../user/user.service';
import { IWorkspaceService } from '../../workspaces/workspaces.service';
import { useAuthService } from '../auth.service';
import { IAuthStore } from '../auth.store';
import { LoginForm } from '../components/LoginForm';

export const LoginContainer = (): ReactElement => {
  const authService = useAuthService();
  const authStore = useInjection(IAuthStore.$);
  const userService = useInjection(IUserService.$);
  const storageService = useInjection(IStorageService.$);
  const workspaceService = useInjection(IWorkspaceService.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const history = useHistory();
  const location = useLocation();

  const lockoutInterval = useRef<NodeJS.Timeout>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(authStore.isAuthenticated);
  const [emailStatus, setEmailStatus] = useState(InputStatus.empty());
  const [passwordStatus, setPasswordStatus] = useState(InputStatus.empty());
  const [externalStatus, setExternalStatus] = useState(InputStatus.empty());
  const [lockoutTime, setLockoutTime] = useState(0);

  useEffect(() => {
    setupLockoutWarning();
    presetUrlData();
    return (): void => {
      clearLockoutInterval();
    };
  }, []);

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    setEmailStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
  };

  const handlePasswordChange = (value: string): void => {
    setPassword(value);
    setPasswordStatus(InputStatus.empty());
    setExternalStatus(InputStatus.empty());
  };

  const validateEmail = (value: string): boolean | undefined => {
    const status = getEmailStatus(value, [], false);
    setEmailStatus(status);
    return status.isValid;
  };

  const validatePassword = (value: string): boolean | undefined => {
    const errorCodes: TranslationKey[] = [];
    if (!value) {
      errorCodes.push('field_cant_be_empty');
    }
    const status = InputStatus.buildFrom(errorCodes);
    setPasswordStatus(status);
    return status.isValid;
  };

  const handleLogin = async (): Promise<void> => {
    setExternalStatus(InputStatus.empty());
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!(isEmailValid && isPasswordValid)) {
      return;
    }

    const result = await withLoaderAsync(async () => authService.authenticate({ userName: email, password }), 'login-button');

    if (result instanceof ACDLError) {
      let errorCodes: TranslationKey[] = result.apiErrorResponse?.errorCodes ?? [];

      if (result.apiErrorResponse?.errorCodes.includes(GLOBAL_ERROR_CODES.EMAIL_NOT_CONFIRMED)) {
        history.push(`/resendConfirmationEmail?email=${encodeURIComponent(email)}`);
      }

      const errorContext = result.apiErrorResponse?.errorContext ?? {};
      if (result.apiErrorResponse?.errorCodes.includes('USER_LOCKED_OUT') && 'lockoutDate' in errorContext) {
        authService.setLockoutDate(errorContext.lockoutDate as string);
        setupLockoutWarning();
        errorCodes = [];
      }

      setExternalStatus(new InputStatus(false, errorCodes));
    } else {
      await withLoaderAsync(async () => {
        await Promise.allSettled([userService.getUserInfoAsync(), userService.getUserSettingsAsync()]);
      }, 'login-button');

      await storageService.initializeCryptoLocalStorage();
      presetUrlData();
      setIsAuthenticated(true);
    }
  };

  const setupLockoutWarning = (): void => {
    if (authStore.lockoutDate !== undefined) {
      const currentDate = new Date();
      const parsedDate = new Date(authStore.lockoutDate);
      const timeDifference = currentDate >= parsedDate ? 0 : Math.ceil((parsedDate.getTime() - currentDate.getTime()) / 1000);
      setLockoutTime(timeDifference);

      lockoutInterval.current = setInterval(() => {
        setLockoutTime((time) => {
          const newTime = time - 1;
          if (!newTime) {
            clearLockoutInterval();
            authService.setLockoutDate(undefined);
          }
          return newTime;
        });
      }, 1000);
    }
  };

  const clearLockoutInterval = (): void => {
    if (lockoutInterval.current) {
      clearInterval(lockoutInterval.current);
      lockoutInterval.current = null;
    }
  };

  const presetUrlData = (): void => {
    const searchParams = qs.parse(location.search.slice(1)) as { workspaceId: string | undefined };
    if (searchParams.workspaceId !== undefined) {
      workspaceService.setPreferredWorkspace(searchParams.workspaceId.toLowerCase());
    }

    authService.setRouteReferrer(location.state);
  };

  if (isAuthenticated) {
    return <Redirect to={authService.getAndClearReferrer()} />;
  }

  return (
    <LoginForm
      handleEmailChange={handleEmailChange}
      handlePasswordChange={handlePasswordChange}
      validateEmail={validateEmail}
      validatePassword={validatePassword}
      handleLogin={handleLogin}
      emailStatus={emailStatus}
      passwordStatus={passwordStatus}
      externalStatus={externalStatus}
      email={email}
      isLoginInProgress={isLoading}
      lockoutTime={lockoutTime}
    />
  );
};
